import { Grid, Stack, TextField, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import SvgIcon from "@mui/material/SvgIcon";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { Download01 } from "@untitled-ui/icons-react/build/esm";
import ArrowLeftIcon from "@untitled-ui/icons-react/build/esm/ArrowLeft";
import { FC, useEffect, useState } from "react";
import { useTillSummary } from "~/hooks/use-till-summary";
import { useGetCurrentCompany } from "~/queries/useGetCurrentCompany";
import { GetFileByIdResults, useGetCurrentFile } from "~/queries/useGetFileById";
import { useGetProductOptions } from "~/queries/useGetProductOptions";
import { useGetProducts } from "~/queries/useGetProducts";
import { InvoicePdfDocument } from "./invoice-pdf-document";
import { useGetFixDiscountsCache } from "~/queries/useGetFixDiscounts";
import { useBoolean } from "usehooks-ts";
import { UpdateFilePDFInput } from "~/types/__generated/gql/graphql";
import toast from "react-hot-toast";
import { useUpdateFilePDF } from "~/queries/useUpdateFilePDF";
export const initPdfState = {
  receiptNote: ""
};
export const InvoicePdfDialog: FC<{
  fixDiscount: number;
  onClose: () => void;
}> = ({
  fixDiscount,
  onClose
}) => {
  const {
    data: company
  } = useGetCurrentCompany();
  const {
    data: file
  } = useGetCurrentFile();
  const {
    updateFileInputSummary
  } = useTillSummary();
  const updateFilePDF = useUpdateFilePDF();
  const {
    data: products = []
  } = useGetProducts();
  const {
    data: productOptions = []
  } = useGetProductOptions();
  const {
    data: fixDiscounts = []
  } = useGetFixDiscountsCache();
  const {
    value: isEditPdfDialogOpen,
    setTrue: openEditPdfDialog,
    setFalse: closeEditPdfDialog
  } = useBoolean(false);
  const [pdfState, setPdfState] = useState<UpdateFilePDFInput>(initPdfState);
  useEffect(() => {
    const inputs: UpdateFilePDFInput = {};
    if (!file) return;
    if (file.receiptNote) inputs.receiptNote = file.receiptNote;
    setPdfState(inputs);
  }, [file]);
  if (!file || !company) return null;
  const pdfDocument = <InvoicePdfDocument itemsSummary={updateFileInputSummary} file={file} fixDiscount={fixDiscount} fixDiscounts={fixDiscounts} company={company} products={products} productOptions={productOptions} isAllApplyFixeDiscount={updateFileInputSummary.$isAllProductsWithFixDiscount} pdfState={pdfState} />;
  return <>
			<Dialog open fullScreen data-sentry-element="Dialog" data-sentry-source-file="invoice-pdf-dialog.tsx">
				<Box sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%"
      }} data-sentry-element="Box" data-sentry-source-file="invoice-pdf-dialog.tsx">
					<Stack direction="row" sx={{
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          p: 2
        }} data-sentry-element="Stack" data-sentry-source-file="invoice-pdf-dialog.tsx">
						<Button color="inherit" startIcon={<SvgIcon>
									<ArrowLeftIcon />
								</SvgIcon>} onClick={onClose} data-sentry-element="Button" data-sentry-source-file="invoice-pdf-dialog.tsx">
							Close
						</Button>
						<Stack direction="row" spacing={2} data-sentry-element="Stack" data-sentry-source-file="invoice-pdf-dialog.tsx">
							{file._receipt?.isFullyPaid && company.isEditReceiptCommentEnabled ? <Button color="primary" variant="contained" onClick={openEditPdfDialog}>
									Edit
								</Button> : !file._receipt?.isFullyPaid && company.isEditInvoiceCommentEnabled && <Button color="primary" variant="contained" onClick={openEditPdfDialog}>
										Edit
									</Button>}

							{!!file && !!company && <PDFDownloadLink document={pdfDocument} fileName="invoice" style={{
              textDecoration: "none"
            }}>
									<Button startIcon={<SvgIcon>
												<Download01 />
											</SvgIcon>} color="primary" variant="contained">
										Download invoice
									</Button>
								</PDFDownloadLink>}
						</Stack>
					</Stack>
					<Box sx={{
          flexGrow: 1
        }} data-sentry-element="Box" data-sentry-source-file="invoice-pdf-dialog.tsx">
						<PDFViewer key={"invoice-pdf-viewer"} height="100%" style={{
            border: "none"
          }} width="100%" data-sentry-element="PDFViewer" data-sentry-source-file="invoice-pdf-dialog.tsx">
							{pdfDocument}
						</PDFViewer>
					</Box>
				</Box>
			</Dialog>
			{isEditPdfDialogOpen && <EditPdfDialog file={file} onClose={closeEditPdfDialog} onSave={async (id: string, values: UpdateFilePDFInput) => {
      await toast.promise(
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async (resolve, reject) => {
        try {
          await updateFilePDF.mutateAsync({
            id: id,
            input: values
          });
          setPdfState(values);
          resolve(null);
        } catch (e) {
          reject(e instanceof Error ? e : new Error("Something went wrong"));
        }
      }), {
        loading: "Updating receipt...",
        success: "Receipt updated successfully",
        error: "Failed to update receipt"
      });
    }} />}
		</>;
};
const EditPdfDialog: FC<{
  file: GetFileByIdResults;
  onClose: () => void;
  onSave: (id: string, obj: UpdateFilePDFInput) => void;
}> = ({
  file,
  onClose,
  onSave
}) => {
  const [state, setState] = useState<UpdateFilePDFInput>({});
  useEffect(() => {
    const inputs: UpdateFilePDFInput = {};
    if (file.receiptNote) inputs.receiptNote = file.receiptNote;
    setState(inputs);
  }, [file]);
  return <Dialog open fullWidth maxWidth="md" data-sentry-element="Dialog" data-sentry-component="EditPdfDialog" data-sentry-source-file="invoice-pdf-dialog.tsx">
			<Box sx={{
      p: 3
    }} data-sentry-element="Box" data-sentry-source-file="invoice-pdf-dialog.tsx">
				<Typography align="left" gutterBottom variant="h5" data-sentry-element="Typography" data-sentry-source-file="invoice-pdf-dialog.tsx">
					Edit Invoice Fields
				</Typography>

				<Grid container spacing={2} mt={2} data-sentry-element="Grid" data-sentry-source-file="invoice-pdf-dialog.tsx">
					{file._receipt?.isFullyPaid ? <>
							<Grid xs={4} item>
								Receipt Notes
							</Grid>
							<Grid xs={8} item>
								<TextField label="Write a note" multiline fullWidth rows={3} value={state.receiptNote} onChange={e => {
              setState({
                ...state,
                receiptNote: e.target.value
              });
            }} variant="outlined" />
							</Grid>
						</> : <>
							<Grid xs={4} item>
								Invoice Notes
							</Grid>
							<Grid xs={8} item>
								<TextField label="Write a note" multiline fullWidth rows={3} value={state.receiptNote} onChange={e => {
              setState({
                ...state,
                receiptNote: e.target.value
              });
            }} variant="outlined" />
							</Grid>
						</>}
				</Grid>
				<Box sx={{
        mt: 2,
        display: "flex",
        justifyContent: "right",
        gap: 2
      }} data-sentry-element="Box" data-sentry-source-file="invoice-pdf-dialog.tsx">
					<Button color="inherit" variant="outlined" onClick={onClose} data-sentry-element="Button" data-sentry-source-file="invoice-pdf-dialog.tsx">
						Cancel
					</Button>
					<Button type="submit" variant="contained" onClick={e => {
          e.preventDefault();
          void onClose();
          void onSave(file._id, state);
        }} data-sentry-element="Button" data-sentry-source-file="invoice-pdf-dialog.tsx">
						Save
					</Button>
				</Box>
			</Box>
		</Dialog>;
};